/**
 * KARGO FİRMALARI — tek kaynak.
 *
 * Eski sistemde liste `order_single.php` içinde sabitti ve firma eklemek kod
 * değişikliği gerektiriyordu. Burada da kodda ama TEK yerde: iki ekran (sipariş
 * detayı ve sevk kuyruğu) aynı listeyi okur, ikisi birbirinden ayrışamaz.
 *
 * `shopifyName` alanı önemli: Shopify fulfillment oluştururken takip
 * bağlantısını OTOMATİK üretmesi için firma adını tanıması gerekiyor. Tanımadığı
 * bir ad gönderilirse takip linki üretilmez ve müşteriye giden e-postada
 * "kargonu takip et" bağlantısı çıkmaz.
 * Shopify'ın tanıdığı adlar: https://shopify.dev/docs/api/admin-graphql — FulfillmentTrackingInput
 */
export interface CargoProvider {
  /** Ekranda görünen ad. */
  label: string;
  /** Shopify'a gönderilen ad. Boşsa `label` gönderilir. */
  shopifyName?: string;
  /** Takip URL kalıbı; `{n}` takip numarasıyla değiştirilir. */
  trackingUrl?: string;
}

export const CARGO_PROVIDERS: readonly CargoProvider[] = [
  {
    label: "UPS Kargo",
    // Shopify "UPS"i yerleşik olarak tanır ve takip linkini kendi üretir.
    shopifyName: "UPS",
    trackingUrl: "https://www.ups.com/track?loc=tr_TR&tracknum={n}",
  },
  {
    label: "Horoz Lojistik",
    shopifyName: "Horoz Lojistik",
    // Horoz sayfası numarayı bağlantıdan almıyor (yalnızca form) — bağlantı
    // sayfayı açar, numara sayfaya yapıştırılır.
    trackingUrl: "https://app3.horoz.com.tr/wsKurumsal/_genel/frmGonderiTakip.aspx?lng=tr",
  },
  {
    label: "Yurtiçi Kargo",
    shopifyName: "Yurtiçi Kargo",
    trackingUrl: "https://www.yurticikargo.com/tr/online-servisler/gonderi-sorgula?code={n}",
  },
];

/**
 * Varsayılan firma. `CARGO_PROVIDERS[0]` yerine bunu kullanın:
 * `noUncheckedIndexedAccess` açık olduğu için dizi indeksi `undefined` olabilir
 * ve her çağrı yerinde kontrol gerekirdi.
 */
/**
 * ★ SIRA İŞ KURALIDIR, alfabetik değil.
 *
 * Gönderilerin çoğu UPS ile yapılıyor, ikinci sırada Horoz Lojistik geliyor.
 * Liste bu sırayla durur ve varsayılan İLK elemandır — ofis her kargo kaydında
 * açılır listeden seçim yapmak zorunda kalmasın.
 *
 * UPS ayrıca API ile TAKİP EDİLEBİLEN tek taşıyıcı (bkz. `functions/src/cargo`):
 * teslim durumu sisteme otomatik düşer. Diğerlerinde takip yalnızca bağlantıdır.
 */
export const DEFAULT_CARGO_PROVIDER: CargoProvider = CARGO_PROVIDERS[0] as CargoProvider;

/**
 * ★ LİSTE BİLEREK KISA. Kullanılan üç firma (UPS · Horoz · Yurtiçi) ve
 * "Özel Sağlayıcı" dışındakiler kaldırıldı — Octovan, Aras, MNG ve Sürat
 * seçeneği ofisi her kargo kaydında gereksiz seçim yapmaya zorluyordu.
 *
 * ⚠ Sunucudaki ad/bağlantı eşleme tabloları (`shopify/write.ts`) OLDUĞU GİBİ
 * DURUYOR: geçmiş siparişlerde bu firmalar kayıtlı ve takip bağlantıları
 * çalışmaya devam etmeli. Buradan kaldırmak yalnızca YENİ kayıtta seçilemez
 * yapar, eskiyi bozmaz.
 */

/** Listede olmayan firma için kullanıcı adı elle yazar. */
export const CUSTOM_PROVIDER = "" as const;

export function providerByLabel(label: string): CargoProvider | undefined {
  return CARGO_PROVIDERS.find((p) => p.label === label);
}

/** Takip numarasından izlenebilir bağlantı üretir; kalıp yoksa null. */
export function buildTrackingUrl(label: string, trackingNumber: string): string | null {
  const p = providerByLabel(label);
  const n = trackingNumber.trim();
  if (!p?.trackingUrl || !n) return null;
  return p.trackingUrl.replace("{n}", encodeURIComponent(n));
}

/** UPS takip numarası kalıbı: `1Z` + 16 harf/rakam. */
const UPS_NUMBER = /^1Z[0-9A-Z]{16}$/;

/** Horoz Lojistik takip numarası: `M` + rakam (ör. M126928). */
export function looksLikeHorozNumber(trackingNumber: string): boolean {
  return /^M\d{5,13}$/i.test(String(trackingNumber ?? "").trim());
}

/** Numaranın kendisi UPS'e mi işaret ediyor? */
export function looksLikeUpsNumber(trackingNumber: string): boolean {
  return UPS_NUMBER.test(String(trackingNumber ?? "").trim().toUpperCase());
}

/**
 * Kayıtlı taşıyıcı adını EKRANDA GÖSTERİLECEK firmaya çevirir.
 *
 * ★ NİÇİN GEREKLİ: canlı veride taşıyıcı alanı literal olarak "Özel Sağlayıcı"
 * yazan kayıtlar var. Bu bir firma adı değil, açılır listedeki SEÇENEĞİN
 * metni — birileri "Özel Sağlayıcı" seçip ad yazmayınca yer tutucu olduğu gibi
 * kaydedilmiş. Sonucu: firma tanınmadığı için takip bağlantısı üretilmiyor,
 * müşteriye giden e-postada firma adı yerine bu ifade çıkıyordu.
 *
 * Takip numarası `1Z…` ise gönderi UPS'tir — numara adı yalanlar, numaraya
 * güvenilir. Aksi hâlde tanınan ad neyse o, hiçbiri değilse boş (özel).
 */
export function resolveProvider(stored: string, trackingNumber: string): string {
  const s = String(stored ?? "").trim();
  if (providerByLabel(s)) return s;
  if (looksLikeUpsNumber(trackingNumber)) return "UPS Kargo";
  // "Other/Diğer" yazılmış ya da boş firma + M… numara → Horoz Lojistik.
  if (looksLikeHorozNumber(trackingNumber) && (isPlaceholderProvider(s) || /^(other|diğer|diger)$/i.test(s))) {
    return "Horoz Lojistik";
  }
  return CUSTOM_PROVIDER;
}

/** Yer tutucu metinler gerçek bir özel firma adı DEĞİLDİR. */
export function isPlaceholderProvider(name: string): boolean {
  const s = String(name ?? "").trim().toLocaleLowerCase("tr");
  return s === "" || s === "özel sağlayıcı" || s === "ozel saglayici";
}
